/* eslint-disable complexity */
import React from "react";
import { Control, useFormContext, useFormState, useWatch } from "react-hook-form";
import { Stack, Box, Tooltip, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Text } from "@imminently/react/ui/core/text";
import { css, Theme } from "@/theme";
import { ScrollIndicator } from "@/components/ScrollIndicator";

import { getFieldRenderer } from "./fieldRegistry";

// Configuration interfaces
export interface LabelOverride {
  label: string;
  value: any;
}

export interface FormFieldConfig {
  id: string;
  label?: string;
  labelOverrides?: LabelOverride[];
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  disabled?: boolean;
  sx?: any;
  showWhen?: {
    field: string;
    value?: any; // If value is undefined, check if field exists/is truthy
    exists?: boolean; // If true, show when field exists/is truthy (regardless of value)
    isDefined?: boolean; // If true, show when field is defined (not "", null, or undefined)
  };
  disableWhen?: {
    field: string;
    value: any;
  };
  clearWhen?: {
    field: string;
    value: any;
  };
  requiredWhen?: {
    field: string;
    value?: any;
    notValue?: any;
  };
  /**
   * When true, changing this field requires a one-off security confirmation
   * before save.
   */
  securityEnabled?: boolean;
}

export interface FormGroupConfig {
  id: string;
  title?: string;
  fields: (string | FormFieldConfig | Omit<FormGroupConfig, "title" | "showWhen">)[];
  layout?: "vertical" | "horizontal" | "fieldset" | "flow";
  showWhen?: { // Added showWhen to group config
    field: string;
    value?: any;
    exists?: boolean;
    isDefined?: boolean; // If true, show when field is defined (not "", null, or undefined)
  };
}

export interface FormSectionConfig {
  id: string;
  title: string;
  headerFields?: (string | FormFieldConfig)[];
  groups: FormGroupConfig[];
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  display?: {
    type: "accordion";
    collapsed?: boolean;
  };
  showWhen?: {
    field: string;
    value?: any;
    exists?: boolean;
    isDefined?: boolean; // If true, show when field is defined (not "", null, or undefined)
  };
}

export interface FormConfig {
  id: string;
  version: number;
  layout?: "single-column" | "three-column";
  sections?: FormSectionConfig[]; // For backward compatibility
  columns?: FormColumnConfig[]; // New column-based structure
}

export interface FormColumnConfig {
  id: string;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  sections: FormSectionConfig[];
  layout?: "vertical" | "horizontal"; // How sections within this column should be arranged
}

// Helper function to normalize field config
const normalizeFieldConfig = (field: string | FormFieldConfig): FormFieldConfig => {
  if (typeof field === "string") {
    return { id: field };
  }
  return field;
};

// Helper function to check if a value is defined (not "", null, or undefined)
const isDefined = (value: any): boolean => {
  return value !== "" && value !== null && value !== undefined;
};

// Individual field renderer component
type DisabledFields = Record<string, { disabled: boolean; reason?: string }>;

const UNUSED_WATCH_FIELD = "__generic_form_unused_watch_field__";

const useOptionalWatch = (control: Control<any>, name?: string) => {
  return useWatch({ control, name: name || UNUSED_WATCH_FIELD });
};

const shouldRenderForCondition = (
  condition: FormFieldConfig["showWhen"] | FormGroupConfig["showWhen"] | FormSectionConfig["showWhen"],
  watchedValue: any,
): boolean => {
  if (!condition) return true;
  if (condition.isDefined) return isDefined(watchedValue);
  if (condition.exists) return !!watchedValue;
  if (condition.value !== undefined) return watchedValue === condition.value;
  return !!watchedValue;
};

const RegistryFieldRenderer: React.FC<{ field: FormFieldConfig; control: Control<any> }> = ({ field, control }) => {
  const renderer = getFieldRenderer(field.id);

  if (!renderer) {
    console.warn(`Field renderer not found for field: ${field.id}`);
    return (
      <Text variant="body2" color="error">
        Unknown field: {field.id}
      </Text>
    );
  }

  return renderer(field, control);
};

const FieldRendererComponent: React.FC<{ field: FormFieldConfig; control: Control<any>; disabledFields?: DisabledFields; suppressAllOrNoneError?: boolean }> = ({ field, control, disabledFields }) => {
  const { setValue } = useFormContext();
  const showWhenValue = useOptionalWatch(control, field.showWhen?.field);
  const disableWhenValue = useOptionalWatch(control, field.disableWhen?.field);
  const requiredWhenValue = useOptionalWatch(control, field.requiredWhen?.field);
  const clearWhenValue = useOptionalWatch(control, field.clearWhen?.field);
  const currentValue = useWatch({ control, name: field.id });
  // Note: suppressAllOrNoneError is passed but suppression is handled in individual field renderers

  // Keep this hook before conditional visibility returns. Some fields toggle
  // visibility while the form is being populated from an existing patient, and
  // calling fewer hooks on the hidden render crashes the create encounter flow.
  React.useEffect(() => {
    if (field.clearWhen && clearWhenValue === field.clearWhen.value) {
      setValue(field.id, "", { shouldDirty: false });
    }
  }, [clearWhenValue, field.clearWhen, field.id, setValue]);

  // Handle conditional visibility
  if (!shouldRenderForCondition(field.showWhen, showWhenValue)) {
    return null;
  }

  // Handle conditional disabling
  let isDisabled = field.disabled || false;
  if (field.disableWhen && disableWhenValue === field.disableWhen.value) {
    isDisabled = true;
  }

  // Handle conditional required
  let isRequired = field.required || false;
  if (field.requiredWhen) {
    if (field.requiredWhen.notValue !== undefined) {
      isRequired = requiredWhenValue !== field.requiredWhen.notValue;
    } else {
      isRequired = requiredWhenValue === field.requiredWhen.value;
    }
  }

  // Compute dynamic label based on labelOverrides
  let computedLabel = field.label;

  if (field.labelOverrides) {
    const override = field.labelOverrides.find(o => o.value === currentValue);
    if (override) {
      computedLabel = override.label;
    }
  }

  // Apply external disabled overrides
  const externalOverride = disabledFields?.[field.id];
  if (externalOverride?.disabled) {
    isDisabled = true;
  }

  const element = (
    <RegistryFieldRenderer
      key={field.id}
      field={{ ...field, label: computedLabel, disabled: isDisabled, required: isRequired }}
      control={control}
    />
  );

  // If disabled with a reason, wrap with Tooltip (span wrapper allows tooltip on disabled controls)
  if (isDisabled && externalOverride?.reason) {
    return (
      <Tooltip title={externalOverride.reason} placement="top" arrow>
        <span>{element}</span>
      </Tooltip>
    );
  }

  return element;
};

const FieldRenderer = React.memo(FieldRendererComponent);

// Flow field wrapper - only renders the Box if the field is visible (handles showWhen at wrapper level)
const FlowFieldWrapper: React.FC<{
  field: FormFieldConfig;
  control: Control<any>;
  disabledFields?: DisabledFields;
  groupError?: { type: string; message: string } | null;
  groupErrorTypes?: Record<string, string>;
}> = ({ field, control, disabledFields, groupError, groupErrorTypes = {} }) => {
  const showWhenValue = useOptionalWatch(control, field.showWhen?.field);

  // Check visibility at wrapper level - if not visible, don't render the Box at all
  if (!shouldRenderForCondition(field.showWhen, showWhenValue)) {
    return null;
  }

  // Field is visible - render with Box wrapper
  return (
    <Box sx={{ flex: 1, minWidth: "200px" }}>
      <FieldRenderer
        field={field}
        control={control}
        disabledFields={disabledFields}
        suppressAllOrNoneError={!!groupError && groupErrorTypes[field.id] === groupError?.type}
      />
    </Box>
  );
};

// Group renderer component
const GroupRendererComponent: React.FC<{ group: FormGroupConfig; control: Control<any>; disabledFields?: DisabledFields }> = ({ group, control, disabledFields }) => {
  const { errors } = useFormState({ control });
  const showWhenValue = useOptionalWatch(control, group.showWhen?.field);
  const layout = group.layout || "vertical";

  // Helper to check if an error is an "all-or-none" validation error
  const isAllOrNoneError = (error: any): boolean => {
    if (!error) return false;
    // Check if error has a type property that matches our test names
    return error.type === "gp-all-or-none" ||
           error.type === "nok-all-or-none" ||
           error.type === "prev-name-all-or-none";
  };

  // Find group-level error (all-or-none errors that should be displayed once per group)
  // Map field IDs to their error types
  const groupErrorTypes: Record<string, string> = {
    gpClinicName: "gp-all-or-none",
    gpDoctorFirstName: "gp-all-or-none",
    gpDoctorLastName: "gp-all-or-none",
    nextOfKinFirstName: "nok-all-or-none",
    nextOfKinLastName: "nok-all-or-none",
    nextOfKinRelationship: "nok-all-or-none",
  };

  // Check if any field in this group has an all-or-none error
  let groupError: { message: string; type: string } | null = null;
  const groupFieldIds = group.fields.map(f => {
    const fieldConfig = normalizeFieldConfig(f);
    return fieldConfig.id;
  });

  for (const fieldId of groupFieldIds) {
    const error = (errors as any)[fieldId];
    if (error && isAllOrNoneError(error)) {
      // Found an all-or-none error, use it as the group error
      groupError = { message: error.message, type: error.type };
      break; // Use the first one found
    }
  }

  // Handle group-level visibility
  if (!shouldRenderForCondition(group.showWhen, showWhenValue)) {
    return null;
  }

  if (layout === "fieldset") {
    // Check if fields contains nested groups (objects with 'id' and 'fields' properties)
    const hasNestedGroups = group.fields.some(field =>
      typeof field === "object" &&
      "id" in field &&
      "fields" in field &&
      !("label" in field) // Groups have 'fields', field configs have 'label'
    );

    return (
      <Box
        component="fieldset"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          m: 0, // Reset margin
          width: "100%"
        }}
      >
        {group.title && (
          <Text
            component="legend"
            variant="subtitle2"
            sx={{ px: 1, fontWeight: 600 }}
          >
            {group.title}
          </Text>
        )}
        {groupError && (
          <Text variant="body2" color="error" sx={{ mb: 1, mt: 0.5 }}>
            {groupError.message}
          </Text>
        )}
        <Stack gap={1} sx={{ width: "100%" }}>
          {hasNestedGroups ? (
            // Render nested groups
            group.fields.map((field, index) => {
              if (typeof field === "object" && "id" in field && "fields" in field && !("label" in field)) {
                // This is a nested group
                const nestedGroup = field as FormGroupConfig;
                return (
                  <GroupRendererComponent
                    key={nestedGroup.id || index}
                    group={nestedGroup}
                    control={control}
                    disabledFields={disabledFields}
                  />
                );
              } else {
                // This is a regular field - check if it's actually a nested group first
                if (typeof field === "object" && "id" in field && "fields" in field && !("label" in field)) {
                  // This is a nested group that wasn't caught by the first check
                  const nestedGroup = field as FormGroupConfig;
                  return (
                    <GroupRendererComponent
                      key={nestedGroup.id || index}
                      group={nestedGroup}
                      control={control}
                      disabledFields={disabledFields}
                    />
                  );
                }
                // This is a regular field (string or FormFieldConfig)
                const fieldConfig = normalizeFieldConfig(field as string | FormFieldConfig);
                return (
                  <FieldRenderer
                    key={`${group.id}-${fieldConfig.id}-${index}`}
                    field={fieldConfig}
                    control={control}
                    disabledFields={disabledFields}
                    suppressAllOrNoneError={!!groupError && groupErrorTypes[fieldConfig.id] === groupError.type}
                  />
                );
              }
            })
          ) : (
            // Render fields directly (original behavior)
            group.fields.map((field, index) => {
              const fieldConfig = normalizeFieldConfig(field);
              return (
                <FieldRenderer
                  key={`${group.id}-${fieldConfig.id}-${index}`}
                  field={fieldConfig}
                  control={control}
                  disabledFields={disabledFields}
                  suppressAllOrNoneError={!!groupError && groupErrorTypes[fieldConfig.id] === groupError.type}
                />
              );
            })
          )}
        </Stack>
      </Box>
    );
  }

  // Flow layout: fields wrap naturally, hidden fields don't take up space
  // Unlike "horizontal" which reserves space for each field, "flow" only renders visible fields
  if (layout === "flow") {
    return (
      <Stack gap={0.5} sx={{ width: "100%" }}>
        {groupError && (
          <Text variant="body2" color="error" sx={{ mb: 0.5 }}>
            {groupError.message}
          </Text>
        )}
        <Stack direction="row" gap={1} sx={{ width: "100%", flexWrap: "wrap" }}>
          {group.fields.map((field, index) => {
            const fieldConfig = normalizeFieldConfig(field);
            return (
              <FlowFieldWrapper
                key={`${group.id}-${fieldConfig.id}-${index}`}
                field={fieldConfig}
                control={control}
                disabledFields={disabledFields}
                groupError={groupError}
                groupErrorTypes={groupErrorTypes}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  }

  if (layout === "horizontal") {
    return (
      <Stack gap={0.5} sx={{ width: "100%" }}>
        {groupError && (
          <Text variant="body2" color="error" sx={{ mb: 0.5 }}>
            {groupError.message}
          </Text>
        )}
        <Stack direction="row" gap={1} sx={{ width: "100%", flexWrap: "wrap" }}>
          {group.fields.map((field, index) => {
            const fieldConfig = normalizeFieldConfig(field);
            // Apply flex values based on field type
            const boxSx: any = {};
            if (fieldConfig.id === "medicareNumber") {
              // 75% minus half the gap
              boxSx.flexBasis = "calc(75% - 4px)";
              boxSx.flexGrow = 0;
              boxSx.flexShrink = 1;
            } else if (fieldConfig.id === "medicarePosition") {
              // 25% minus half the gap
              boxSx.flexBasis = "calc(25% - 4px)";
              boxSx.flexGrow = 0;
              boxSx.flexShrink = 1;
            } else {
              boxSx.flex = 1;
              boxSx.minWidth = "200px";
            }
            return (
              <Box key={`${group.id}-${fieldConfig.id}-${index}`} sx={boxSx}>
                <FieldRenderer
                  field={fieldConfig}
                  control={control}
                  disabledFields={disabledFields}
                  suppressAllOrNoneError={!!groupError && groupErrorTypes[fieldConfig.id] === groupError.type}
                />
              </Box>
            );
          })}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={1} sx={{ width: "100%" }}>
      {groupError && (
        <Text variant="body2" color="error" sx={{ mb: 0.5 }}>
          {groupError.message}
        </Text>
      )}
      {group.fields.map((field, index) => {
        const fieldConfig = normalizeFieldConfig(field);
        return (
          <FieldRenderer
            key={`${group.id}-${fieldConfig.id}-${index}`}
            field={fieldConfig}
            control={control}
            disabledFields={disabledFields}
            suppressAllOrNoneError={!!groupError && groupErrorTypes[fieldConfig.id] === groupError.type}
          />
        );
      })}
    </Stack>
  );
};

const GroupRenderer = React.memo(GroupRendererComponent);

// Section renderer component
const SectionRendererComponent: React.FC<{ section: FormSectionConfig; control: Control<any>; disabledFields?: DisabledFields }> = ({ section, control, disabledFields }) => {
  const showWhenValue = useOptionalWatch(control, section.showWhen?.field);
  // Default to expanded (true) unless explicitly set to collapsed
  const initialExpanded = section.display?.collapsed === true ? false : true;
  const [expanded, setExpanded] = React.useState<boolean>(initialExpanded);

  // Handle section-level visibility
  if (!shouldRenderForCondition(section.showWhen, showWhenValue)) {
    return null;
  }

  const styles = {
    paper: (theme: Theme) => css`
      padding: ${theme.spacing(2)};
      background-color: ${theme.palette.background.paper};
      border-radius: ${theme.shape.borderRadius}px;
      border: 1px solid ${theme.palette.divider};
    `,
  };

  // Build grid props based on section configuration
  const gridProps = {
    xs: section.xs || 12,
    sm: section.sm || section.xs || 12,
    md: section.md || section.sm || section.xs || 12,
    lg: section.lg || section.md || section.sm || section.xs || 12,
    xl: section.xl || section.lg || section.md || section.sm || section.xs || 12,
  };

  const sectionContent = (
    <Stack gap={2}>
      {section.groups.map((group) => (
        <GroupRenderer key={group.id} group={group} control={control} disabledFields={disabledFields} />
      ))}
    </Stack>
  );

  const sectionHeader = (
    <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%", flexWrap: "wrap" }}>
      <Text variant="h6">{section.title}</Text>
      {section.headerFields && section.headerFields.length > 0 && (
        <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: "wrap" }}>
          {section.headerFields.map((field, index) => {
            const fieldConfig = normalizeFieldConfig(field);
            return (
              <FieldRenderer
                key={[section.id, "header", fieldConfig.id, index].join("-")}
                field={fieldConfig}
                control={control}
                disabledFields={disabledFields}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );

  const baseSx = {
    width: "100%",
    flexBasis: `${(gridProps.xs / 12) * 100}%`,
    "@media (min-width: 600px)": {
      flexBasis: `${(gridProps.sm / 12) * 100}%`,
    },
    "@media (min-width: 900px)": {
      flexBasis: `${(gridProps.md / 12) * 100}%`,
    },
    "@media (min-width: 1200px)": {
      flexBasis: `${(gridProps.lg / 12) * 100}%`,
    },
    "@media (min-width: 1536px)": {
      flexBasis: `${(gridProps.xl / 12) * 100}%`,
    },
  };

  // Render as accordion if display type is accordion
  if (section.display?.type === "accordion") {
    return (
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{
          ...baseSx,
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
          },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          backgroundColor: "background.paper",
          "&:hover": {
            backgroundColor: "background.paper",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: (theme) => theme.spacing(2),
            paddingRight: (theme) => theme.spacing(1),
            minHeight: "auto",
            "&.Mui-expanded": {
              minHeight: "auto",
            },
            "&:hover": {
              backgroundColor: "transparent",
            },
            "& .MuiAccordionSummary-content": {
              margin: 0,
              "&.Mui-expanded": {
                margin: 0,
              },
            },
          }}
        >
          {sectionHeader}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: (theme) => theme.spacing(2),
            paddingTop: 0,
          }}
        >
          {sectionContent}
        </AccordionDetails>
      </Accordion>
    );
  }

  // Default rendering (non-accordion)
  return (
    <Stack css={styles.paper} gap={1} sx={baseSx}>
      {sectionHeader}
      {sectionContent}
    </Stack>
  );
};

const SectionRenderer = React.memo(SectionRendererComponent);

// Column renderer component
const ColumnRenderer: React.FC<{ column: FormColumnConfig; control: Control<any>; disabledFields?: DisabledFields }> = ({ column, control, disabledFields }) => {
  // Build grid props based on column configuration
  const gridProps = {
    xs: column.xs || 12,
    sm: column.sm || column.xs || 12,
    md: column.md || column.sm || column.xs || 12,
    lg: column.lg || column.md || column.sm || column.xs || 12,
    xl: column.xl || column.lg || column.md || column.sm || column.xs || 12,
  };

  return (
    <Stack
      gap={2}
      sx={{
        width: "100%",
        flexBasis: `${(gridProps.xs / 12) * 100}%`,
        "@media (min-width: 600px)": {
          flexBasis: `${(gridProps.sm / 12) * 100}%`,
        },
        "@media (min-width: 900px)": {
          flexBasis: `${(gridProps.md / 12) * 100}%`,
        },
        "@media (min-width: 1200px)": {
          flexBasis: `${(gridProps.lg / 12) * 100}%`,
        },
        "@media (min-width: 1536px)": {
          flexBasis: `${(gridProps.xl / 12) * 100}%`,
        },
      }}
    >
      {column.sections.map((section) => (
        <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
      ))}
    </Stack>
  );
};

// Main form renderer component
export const GenericFormRenderer: React.FC<{ config: FormConfig; disabledFields?: DisabledFields }> = ({ config, disabledFields }) => {
  const { control } = useFormContext();

  // Handle new column-based layout
  if (config.columns) {
    return (
      <>
        <Stack direction="row" alignItems="flex-start" gap={2} flex={1}>
          {config.columns.map((column) => {
            // Handle horizontal layout for sections within a column
            if (column.layout === "horizontal") {
              // Build grid props for the column itself
              const columnGridProps = {
                xs: column.xs || 12,
                sm: column.sm || column.xs || 12,
                md: column.md || column.sm || column.xs || 12,
                lg: column.lg || column.md || column.sm || column.xs || 12,
                xl: column.xl || column.lg || column.md || column.sm || column.xs || 12,
              };

              return (
                <Box
                  key={column.id}
                  sx={{
                    width: "100%",
                    flexBasis: `${(columnGridProps.xs / 12) * 100}%`,
                    "@media (min-width: 600px)": {
                      flexBasis: `${(columnGridProps.sm / 12) * 100}%`,
                    },
                    "@media (min-width: 900px)": {
                      flexBasis: `${(columnGridProps.md / 12) * 100}%`,
                    },
                    "@media (min-width: 1200px)": {
                      flexBasis: `${(columnGridProps.lg / 12) * 100}%`,
                    },
                    "@media (min-width: 1536px)": {
                      flexBasis: `${(columnGridProps.xl / 12) * 100}%`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" gap={2} flex={1}>
                    {column.sections.map((section) => (
                      <Box key={section.id} sx={{ flex: 1 }}>
                        <SectionRenderer section={section} control={control} disabledFields={disabledFields} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              );
            }

            // Default vertical column rendering
            return <ColumnRenderer key={column.id} column={column} control={control} disabledFields={disabledFields} />;
          })}
        </Stack>

        {/* Scroll indicator */}
        <ScrollIndicator />
      </>
    );
  }

  // Handle three-column layout (legacy)
  if (config.layout === "three-column" && config.sections) {
    // Separate full-width sections from column sections
    const fullWidthSections = config.sections.filter(section => section.xs === 12 && !section.md);
    const columnSections = config.sections.filter(section => section.xs !== 12 || section.md);

    // Group column sections by column based on their order
    const column1Sections = columnSections.filter((_, index) => index % 3 === 0);
    const column2Sections = columnSections.filter((_, index) => index % 3 === 1);
    const column3Sections = columnSections.filter((_, index) => index % 3 === 2);

    return (
      <>
        <Stack direction="row" alignItems="flex-start" gap={2} flex={1}>
          {/* Column 1 */}
          <Stack gap={2} flex={1}>
            {column1Sections.map((section) => (
              <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
            ))}
          </Stack>

          {/* Column 2 */}
          <Stack gap={2} flex={1}>
            {column2Sections.map((section) => (
              <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
            ))}
          </Stack>

          {/* Column 3 */}
          <Stack gap={2} flex={1}>
            {column3Sections.map((section) => (
              <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
            ))}
          </Stack>
        </Stack>

        {/* Full-width sections */}
        {fullWidthSections.map((section) => (
          <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
        ))}

        {/* Scroll indicator */}
        <ScrollIndicator />
      </>
    );
  }

  // Original single-column layout with horizontal sections (legacy)
  if (config.sections) {
    const horizontalSections = ["assessment", "visit-details", "additional-info"];

    // Find the position where horizontal sections start
    const horizontalStartIndex = config.sections.findIndex(section => horizontalSections.includes(section.id));

    // Split sections into before, horizontal, and after
    const beforeSections = config.sections.slice(0, horizontalStartIndex);
    const horizontalSectionGroup = config.sections.slice(horizontalStartIndex, horizontalStartIndex + 3);
    const afterSections = config.sections.slice(horizontalStartIndex + 3);

    return (
      <>
        <Stack gap={2}>
          {/* Render sections before horizontal group */}
          {beforeSections.map((section) => (
            <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
          ))}

          {/* Render horizontal sections in a row */}
          {horizontalSectionGroup.length > 0 && (
            <Stack direction="row" alignItems="flex-start" gap={2} flex={1}>
              {horizontalSectionGroup.map((section) => (
                <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
              ))}
            </Stack>
          )}

          {/* Render sections after horizontal group */}
          {afterSections.map((section) => (
            <SectionRenderer key={section.id} section={section} control={control} disabledFields={disabledFields} />
          ))}
        </Stack>

        {/* Scroll indicator */}
        <ScrollIndicator />
      </>
    );
  }
};

// Hook to get form configuration
export const useFormConfig = (): FormConfig | null => {
  // For now, we'll return null and handle configs in the component
  // Later this can be extended to fetch from API or context
  return null;
};
